import { safeSetDoc } from '../utils';
import { createContext, useContext, useEffect, useReducer, useMemo } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { appId, APPROVED_EMAILS } from '../constants';

const AuthContext = createContext();

const initialState = {
  user: null,
  profile: null,
  loading: true,
  error: null,
  isBypass: false,
};

function authReducer(state, action) {
  switch (action.type) {
    case 'SET_USER':
      return {
        ...state,
        user: action.payload.user,
        profile: action.payload.profile,
        loading: false,
        isBypass: false,
        error: null,
      };
    case 'UPDATE_PROFILE_LOCAL':
      return {
        ...state,
        profile: { ...state.profile, ...action.payload },
      };
    case 'SET_BYPASS':
      return {
        ...state,
        user: { uid: 'test-uid-123', email: 'test@example.com' },
        profile: { name: 'Test User', avatar: '🤖', motivationalMessage: 'Test Mode Active' },
        loading: false,
        isBypass: true,
        error: null,
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'LOGOUT':
      return { ...initialState, loading: false };
    default:
      return state;
  }
}

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.search.includes('access=')) {
      window.testAuthDispatch = dispatch;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('access') === 'kx92j') {
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        dispatch({ type: 'SET_BYPASS' });
        return;
      }
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        if (!APPROVED_EMAILS.includes(user.email?.toLowerCase())) {
          console.warn("User email not in allowlist:", user.email);
          await signOut(auth);
          dispatch({ type: 'SET_ERROR', payload: 'ACCESS_DENIED' });
          return;
        }

        try {
          const userDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          let profile;
          if (userDoc.exists()) {
            profile = userDoc.data();
            // Ensure existing users have a sync token
            if (!profile.calendarSyncToken) {
              const syncToken = Array.from(crypto.getRandomValues(new Uint8Array(20)))
                .map((b) => b.toString(16).padStart(2, '0'))
                .join('');
              profile.calendarSyncToken = syncToken;
              await safeSetDoc(userDocRef, { calendarSyncToken: syncToken }, { merge: true });
            }
          } else {
            // Provision new profile
            const syncToken = Array.from(crypto.getRandomValues(new Uint8Array(20)))
              .map((b) => b.toString(16).padStart(2, '0'))
              .join('');

            profile = {
              id: user.uid,
              name: user.displayName || 'User',
              avatar: user.photoURL || '👤',
              email: user.email,
              calendarSyncToken: syncToken,
              createdAt: serverTimestamp(),
              lastSeen: serverTimestamp(),
            };
            await safeSetDoc(userDocRef, profile);
          }
          dispatch({ type: 'SET_USER', payload: { user, profile } });
        } catch (err) {
          console.error('Error fetching profile:', err);
          dispatch({
            type: 'SET_USER',
            payload: { user, profile: { name: user.displayName || 'User', avatar: '👤' } },
          });
        }
      } else {
        dispatch({ type: 'SET_USER', payload: { user: null, profile: null } });
      }
    });

    return () => unsubscribe();
  }, []);

  const username = state.profile?.name || state.user?.displayName || 'User';

  const contextValue = useMemo(
    () => ({ ...state, username, dispatch }),
    [state, username, dispatch]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {state.error === 'ACCESS_DENIED' ? (
        <div style={{ display: 'flex', height: '100vh', width: '100vw', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000', color: '#fff', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
          <h1>Access not approved — contact Bert</h1>
        </div>
      ) : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
